import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ICartSession } from "../../typings/interfaces/pos/cart-session";
import {
  IBillItemLimit,
  IEditPinTagCapacity,
  isBillItemLimitExceeded,
} from "../../util/bill-item-limit";
import {
  addBillNoteAction,
  addCartItem,
  addSession,
  deleteBillNoteAction,
  deleteDraftAction,
  editBillAction,
  getBillByIdAction,
  getBillItemIdListAction,
  getBillNoteByIdAction,
  getBillPaymentHistoryAction,
  getCashierDraftsAction,
  newBillAction,
  newPaymentAction,
  pickUpItemAction,
  removeCartItem,
  removeSession,
  saveDraftAction,
  updateBillDiscountAction,
  updateBillNoteAction,
  updateCartSessionItem,
} from "../actions/cartAction";
import { CartItem } from "../../typings/interfaces/pos/cart-item";
import { BaseResponse, CategoryResponse, ICustomerBill } from "../../typings";
import { ICustomerResponse } from "../../typings";
import { IBillResponse } from "../../typings/interfaces/responses/new-bill-response";
import { BillNoteResponse } from "../../typings/interfaces/responses/bill-note-response";
import { temporaryBillNotesInterface } from "../../typings/interfaces/requests/bill-note-requests";
import { CustomizationTypes } from "../../typings/enum/customizationTypes";
import {
  IBillPaymentHistory,
  PaymentCreateResponse,
} from "../../typings/interfaces/responses/paymentResponse";
import { IHalfPayment } from "../../typings/interfaces/payment/halfPayment";
import { IDraft } from "../../typings/interfaces/pos/draft";
import {
  IBillItemIdListResponse,
  IPickupItemResponse,
} from "../../typings/interfaces/responses/pickupResponses";
import { IBillPayment } from "../../components/pos/payment-modal/payment-modal";

export interface CartState {
  // Set when an add/qty change would overfill the bill; drives the dialog
  billItemLimitExceeded: IBillItemLimit | null;
  // Pin tags the ticket being edited started with; caps its items (3.m/3.n)
  editPinTagCapacity: IEditPinTagCapacity | null;
  // Bumped to ask the session nav to start a new ticket (e.g. from the
  // Maximum Pieces dialog); the nav runs its usual New session flow
  newSessionRequest: number;
  cart: ICartSession[];
  drafts: IDraft[];
  selectedSession: ICartSession | null;
  selectedCartItem: CartItem | null;
  selectedServiceType: CategoryResponse | null;
  selectedCustomizationType: CustomizationTypes;
  previousServiceType: CategoryResponse | null;
  selectedCustomer: ICustomerResponse | null;
  bills: Array<IBillResponse> | null;
  billsLoading: boolean;
  billsError: string | null;
  temporaryBillNotes: Array<temporaryBillNotesInterface>;
  cartMode: "check-in" | "pickup";
  isRedoModalOpen: boolean;

  billById: IBillResponse | null;
  billByIdLoading: boolean;
  billByIdError: string | null;

  addBillNoteResponse: BaseResponse<any> | null;
  addBillNoteLoading: boolean;
  addBillNoteError: string | null;

  updateBillNoteResponse: BaseResponse<any> | null;
  updateBillNoteLoading: boolean;
  updateBillNoteError: string | null;

  billNotes: Array<BillNoteResponse> | null;
  billNotesLoading: boolean;
  billNotesError: string | null;

  deleteBillNoteResponse: BaseResponse<any> | null;
  deleteBillNoteLoading: boolean;
  deleteBillNoteError: string | null;

  paymentCreateResponse: BaseResponse<PaymentCreateResponse> | null;
  paymentCreateLoading: boolean;
  paymentCreateError: string | null;

  paymentHalfDetails: IHalfPayment[];

  cartHistorySelectedRows: ICustomerBill[];

  pickUpItemResponse: IPickupItemResponse | null;
  pickUpItemLoading: boolean;
  pickUpItemError: string | null;

  billItemIdListLoading: boolean;
  billItemIdListError: string | null;

  billItemIdList: {
    billId: number;
    billItemIdList: IBillItemIdListResponse[];
  }[];

  refreshCustomerHistory: boolean;

  billPaymentDetails: IBillPaymentHistory | null;
  billPaymentDetailsLoading: boolean;
  billPaymentDetailsError: string | null;

  editBillLoading: boolean;
  editBillError: string | null;
  editBillResponse: IBillResponse | null;

  updateBillDiscountLoading: boolean;
  updateBillDiscountError: string | null;

  saveDraftResponse: IDraft | null;
  saveDraftLoading: boolean;
  saveDraftError: string | null;

  getCashierDrafts: any[] | null;
  getCashierDraftsLoading: boolean;
  getCashierDraftsError: string | null;

  deleteDraftLoading: boolean;
  deleteDraftError: string | null;
  deleteDraftResponse: number | null;
}

const initialState: CartState = {
  billItemLimitExceeded: null,
  editPinTagCapacity: null,
  newSessionRequest: 0,
  cart: [],
  drafts: [],
  selectedSession: null,
  selectedCartItem: null,
  selectedServiceType: null,
  selectedCustomizationType: CustomizationTypes.INSTRUCTIONS,
  previousServiceType: null,
  selectedCustomer: null,
  temporaryBillNotes: [],
  cartMode: "check-in",
  isRedoModalOpen: false,

  bills: null,
  billsLoading: false,
  billsError: null,

  billById: null,
  billByIdLoading: false,
  billByIdError: null,

  addBillNoteResponse: null,
  addBillNoteLoading: false,
  addBillNoteError: null,

  updateBillNoteResponse: null,
  updateBillNoteLoading: false,
  updateBillNoteError: null,

  billNotes: [],
  billNotesLoading: false,
  billNotesError: null,

  deleteBillNoteResponse: null,
  deleteBillNoteLoading: false,
  deleteBillNoteError: null,

  paymentHalfDetails: [],

  paymentCreateResponse: null,
  paymentCreateLoading: false,
  paymentCreateError: null,

  cartHistorySelectedRows: [],

  pickUpItemResponse: null,
  pickUpItemLoading: false,
  pickUpItemError: null,

  billItemIdListLoading: false,
  billItemIdListError: null,

  billItemIdList: [],

  refreshCustomerHistory: false,

  billPaymentDetails: null,
  billPaymentDetailsLoading: false,
  billPaymentDetailsError: null,

  editBillError: null,
  editBillLoading: false,
  editBillResponse: null,

  updateBillDiscountLoading: false,
  updateBillDiscountError: null,

  saveDraftResponse: null,
  saveDraftLoading: false,
  saveDraftError: null,

  getCashierDrafts: null,
  getCashierDraftsLoading: false,
  getCashierDraftsError: null,

  deleteDraftLoading: false,
  deleteDraftError: null,
  deleteDraftResponse: null,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    clearBillItemLimitExceeded: (state) => {
      state.billItemLimitExceeded = null;
    },
    setEditPinTagCapacity: (state, action: { payload: IEditPinTagCapacity | null }) => {
      state.editPinTagCapacity = action.payload;
    },
    // The check-in summary decides Special / Regular per ticket (from its pin
    // tag); kept on the session so the totals include the Special charge.
    setSessionsSpecial: (
      state,
      action: PayloadAction<{ sessionId: string; isSpecial: boolean }[]>
    ) => {
      action.payload.forEach(({ sessionId, isSpecial }) => {
        const session = state.cart.find((s) => s.sessionId === sessionId);
        if (session && !!session.isSpecial !== isSpecial) session.isSpecial = isSpecial;
        const selected = state.selectedSession;
        if (selected?.sessionId === sessionId && !!selected.isSpecial !== isSpecial) {
          selected.isSpecial = isSpecial;
        }
      });
    },
    requestNewSession: (state) => {
      state.newSessionRequest += 1;
    },
    setSelectedSession: (state, action) => {
      state.selectedSession = action.payload;
      state.selectedCartItem = null;
    },
    setSelectedCartItem: (state, action) => {
      state.selectedCartItem = action.payload;
    },
    setSelectedServiceType: (state, action) => {
      state.previousServiceType = state.selectedServiceType;
      state.selectedServiceType = action.payload;
    },
    // setPreviousServiceType: (state, action) => {
    //   state.previousServiceType = action.payload;
    // },
    setSelectedCustomizationType: (state, action) => {
      state.selectedCustomizationType = action.payload;
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    setCartFromDraft: (state, action: PayloadAction<ICartSession[]>) => {
      const sessions = action.payload ?? [];
      state.cart = sessions;
      state.selectedSession = sessions.length
        ? sessions[sessions.length - 1]
        : null;
      state.selectedCartItem = null;
    },
    clearCartOnly: (state) => {
      state.cart = [];
      state.selectedSession = null;
      state.selectedCartItem = null;
    },

    pruneEmptySessions(state) {
      const isEmpty = (s: any) => (s?.cartItems?.length ?? 0) === 0;
      const empties = state.cart.filter(isEmpty);
      if (empties.length <= 1) return;

      const keepIds = new Set<string>();
      if (state.selectedSession && isEmpty(state.selectedSession)) {
        keepIds.add(state.selectedSession.sessionId);
      } else if (empties.length) {
        keepIds.add(empties[empties.length - 1].sessionId);
      }

      const newCart = state.cart.filter(
        (s) => !isEmpty(s) || keepIds.has(s.sessionId)
      );

      const prevSelectedId = state.selectedSession?.sessionId;
      state.cart = newCart;
      state.selectedSession =
        newCart.find((s) => s.sessionId === prevSelectedId) ??
        newCart[newCart.length - 1] ??
        null;
      state.selectedCartItem = null;
    },
    clearSession: (state) => {
      state.selectedSession = null;
      state.selectedCartItem = null;
      state.cart = [];
      state.selectedCustomer = null;
      state.paymentHalfDetails = [];
      state.editPinTagCapacity = null;
    },
    updatePinTag: (
      state,
      action: {
        payload: {
          sessionId: string;
          pinTag: { color: string; tags: string[]; date: string | null };
        };
      }
    ) => {
      const { sessionId, pinTag } = action.payload;
      const session = state.cart.find((s) => s.sessionId === sessionId);
      if (session) {
        session.pinTag = pinTag;
      }
    },

    setCartMode: (state, action) => {
      state.cartMode = action.payload;
    },

    setDrafts: (state, action) => {
      state.drafts = action.payload;
    },
    discardCustomerDraft: (state, action) => {
      state.drafts = state.drafts.filter(
        (draft) => draft.customer.id !== action.payload
      );
    },
    clearDraftedSessions: (state) => {
      state.drafts = [];
    },
    setTemporaryBillNotes: (state, action) => {
      state.addBillNoteLoading = true;
      state.temporaryBillNotes = action.payload;
      state.addBillNoteLoading = false;
    },
    setHalfPayementDetails: (state, action) => {
      state.paymentHalfDetails = action.payload;
    },
    clearBill: (state) => {
      state.bills = null;
      state.billsLoading = false;
      state.billsError = null;
    },
    setCartHistorySelectedRows: (state, action) => {
      state.cartHistorySelectedRows = action.payload;
    },
    setBillItemIdList: (state, action) => {
      state.billItemIdList = action.payload;
    },
    setRefreshCustomerHistory: (state, action) => {
      state.refreshCustomerHistory = action.payload;
    },
    setIsRedoModalOpen: (state, action) => {
      state.isRedoModalOpen = action.payload;
    },
    clearBillPaymentDetails: (state) => {
      state.billPaymentDetails = null;
      state.billPaymentDetailsLoading = false;
      state.billPaymentDetailsError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addSession.fulfilled, (state, action) => {
      state.cart = action.payload;
      state.selectedSession =
        action.payload.find(
          (session) => session.sessionId === action.meta.arg.sessionId
        ) ?? action.payload[action.payload.length - 1];
      state.selectedCartItem = null;
    });
    builder.addCase(addCartItem.fulfilled, (state, action) => {
      state.cart = action.payload.copiedCartSessions;
      state.selectedSession = action.payload.cartSession;
      state.selectedCartItem = null;
    });
    builder.addCase(addCartItem.rejected, (state, action) => {
      if (!isBillItemLimitExceeded(action.payload)) return;
      const { maxItems, serviceName, pinTags } = action.payload;
      state.billItemLimitExceeded = { maxItems, serviceName, pinTags };
      // Drop the item being built - it can't go on this bill
      const draftId = state.selectedCartItem?.finalized ? null : state.selectedCartItem?.itemId;
      if (draftId) {
        const session = state.cart.find((s) => s.sessionId === state.selectedSession?.sessionId);
        if (session) {
          session.cartItems = session.cartItems.filter((item) => item.itemId !== draftId);
          state.selectedSession = session;
        }
      }
      state.selectedCartItem = null;
    });
    builder.addCase(updateCartSessionItem.rejected, (state, action) => {
      if (!isBillItemLimitExceeded(action.payload)) return;
      const { maxItems, serviceName, pinTags } = action.payload;
      state.billItemLimitExceeded = { maxItems, serviceName, pinTags };
    });
    builder.addCase(removeSession.fulfilled, (state, action) => {
      state.cart = action.payload;
      state.selectedSession = action.payload[action.payload.length - 1];
      state.selectedCartItem = null;
    });
    builder.addCase(updateCartSessionItem.fulfilled, (state, action) => {
      state.cart = action.payload.clonedCart;
      state.selectedCartItem = action.payload.cartItem;
      state.selectedSession = action.payload.session;
    });
    builder.addCase(removeCartItem.fulfilled, (state, action) => {
      state.cart = action.payload.clonedCart;
      state.selectedSession = action.payload.session;
      state.selectedCartItem = null;
    });

    builder
      .addCase(newBillAction.pending, (state) => {
        state.billsLoading = true;
        state.billsError = null;
        state.bills = null;
      })
      .addCase(newBillAction.fulfilled, (state, action) => {
        state.billsLoading = false;
        state.billsError = null;
        state.bills = action.payload;
      })
      .addCase(newBillAction.rejected, (state, action) => {
        state.billsLoading = false;
        // Rejections carry the reason in the payload; action.error.message is
        // just "Rejected"
        const payload = action.payload as { message?: string } | string | undefined;
        state.billsError =
          (typeof payload === "string" ? payload : payload?.message) ||
          action.error.message ||
          "Something went wrong";
        state.bills = null;
      })

      .addCase(getBillByIdAction.pending, (state) => {
        state.billByIdLoading = true;
        state.billByIdError = null;
        state.billById = null;
      })
      .addCase(getBillByIdAction.fulfilled, (state, action) => {
        state.billByIdLoading = false;
        state.billByIdError = null;
        state.billById = action.payload;
      })
      .addCase(getBillByIdAction.rejected, (state, action) => {
        state.billByIdLoading = false;
        state.billByIdError = action.error.message ?? "Something went wrong";
        state.billById = null;
      })

      .addCase(getBillNoteByIdAction.pending, (state: CartState) => {
        state.billNotesLoading = true;
        state.billNotesError = null;
        state.billNotes = null;
      })
      .addCase(getBillNoteByIdAction.fulfilled, (state: CartState, action) => {
        state.billNotesLoading = false;
        state.billNotesError = null;
        state.billNotes = action.payload;
      })
      .addCase(getBillNoteByIdAction.rejected, (state: CartState, action) => {
        state.billNotesLoading = false;
        state.billNotesError = action.error.message ?? "Something went wrong";
        state.billNotes = null;
      })

      .addCase(addBillNoteAction.pending, (state) => {
        state.addBillNoteLoading = true;
        state.addBillNoteError = null;
        state.addBillNoteResponse = null;
      })
      .addCase(addBillNoteAction.fulfilled, (state, action) => {
        state.addBillNoteLoading = false;
        state.addBillNoteError = null;
        state.addBillNoteResponse = action.payload;
      })
      .addCase(addBillNoteAction.rejected, (state, action) => {
        state.addBillNoteLoading = false;
        state.addBillNoteError = action.error.message ?? "Something went wrong";
        state.addBillNoteResponse = null;
      })

      .addCase(updateBillNoteAction.pending, (state) => {
        state.updateBillNoteLoading = true;
        state.updateBillNoteError = null;
        state.updateBillNoteResponse = null;
      })
      .addCase(updateBillNoteAction.fulfilled, (state, action) => {
        state.updateBillNoteLoading = false;
        state.updateBillNoteError = null;
        state.updateBillNoteResponse = action.payload;
      })
      .addCase(updateBillNoteAction.rejected, (state, action) => {
        state.updateBillNoteLoading = false;
        state.updateBillNoteError =
          action.error.message ?? "Something went wrong";
        state.updateBillNoteResponse = null;
      })

      .addCase(deleteBillNoteAction.pending, (state) => {
        state.deleteBillNoteLoading = true;
        state.deleteBillNoteError = null;
        state.deleteBillNoteResponse = null;
      })
      .addCase(deleteBillNoteAction.fulfilled, (state, action) => {
        state.deleteBillNoteLoading = false;
        state.deleteBillNoteError = null;
        state.deleteBillNoteResponse = action.payload;
      })
      .addCase(deleteBillNoteAction.rejected, (state, action) => {
        state.deleteBillNoteLoading = false;
        state.deleteBillNoteError =
          action.error.message ?? "Something went wrong";
        state.deleteBillNoteResponse = null;
      })

      .addCase(newPaymentAction.pending, (state) => {
        state.paymentCreateLoading = true;
        state.paymentCreateError = null;
        state.paymentCreateResponse = null;
      })
      .addCase(newPaymentAction.fulfilled, (state, action) => {
        state.paymentCreateLoading = false;
        state.paymentCreateError = null;
        state.paymentCreateResponse = action.payload;
        state.refreshCustomerHistory = true;
      })
      .addCase(newPaymentAction.rejected, (state, action) => {
        state.paymentCreateLoading = false;
        state.paymentCreateError =
          action.error.message ?? "Something went wrong";
        state.paymentCreateResponse = null;
      });

    builder
      .addCase(pickUpItemAction.pending, (state) => {
        state.pickUpItemLoading = true;
        state.pickUpItemError = null;
        state.pickUpItemResponse = null;
      })
      .addCase(pickUpItemAction.fulfilled, (state, action) => {
        state.pickUpItemLoading = false;
        state.pickUpItemError = null;
        state.pickUpItemResponse = action.payload;
      })
      .addCase(pickUpItemAction.rejected, (state, action) => {
        state.pickUpItemLoading = false;
        state.pickUpItemError = action.error.message ?? "Something went wrong";
        state.pickUpItemResponse = null;
      });

    builder
      .addCase(getBillItemIdListAction.pending, (state) => {
        state.billItemIdListLoading = true;
        state.billItemIdListError = null;
      })
      .addCase(getBillItemIdListAction.fulfilled, (state, action) => {
        state.billItemIdListLoading = false;
        state.billItemIdListError = null;
        // Upsert by billId: replace any existing entry for this bill instead
        // of blindly appending, so re-fetching the same bill's item list
        // (e.g. reopening the Pickup modal for the same bill within one SPA
        // session) doesn't leave stale entries ahead of `.find()` consumers.
        state.billItemIdList = [
          ...state.billItemIdList.filter(
            (entry) => entry.billId !== action.payload.billId
          ),
          action.payload,
        ];
      })
      .addCase(getBillItemIdListAction.rejected, (state, action) => {
        state.billItemIdListLoading = false;
        state.billItemIdListError =
          action.error.message ?? "Something went wrong";
      });

    builder
      .addCase(getBillPaymentHistoryAction.pending, (state) => {
        state.billPaymentDetails = null;
        state.billPaymentDetailsLoading = true;
        state.billPaymentDetailsError = null;
      })
      .addCase(getBillPaymentHistoryAction.fulfilled, (state, action) => {
        state.billPaymentDetailsLoading = false;
        state.billPaymentDetailsError = null;
        state.billPaymentDetails = action.payload;
      })
      .addCase(getBillPaymentHistoryAction.rejected, (state, action) => {
        state.billPaymentDetailsLoading = false;
        state.billPaymentDetailsError =
          action.error.message ?? "Something went wrong";
        state.billPaymentDetails = null;
      });

    builder
      .addCase(editBillAction.pending, (state) => {
        state.editBillLoading = true;
        state.editBillError = null;
        state.editBillResponse = null;
      })
      .addCase(editBillAction.fulfilled, (state, action) => {
        state.editBillLoading = false;
        state.editBillError = null;
        state.editBillResponse = action.payload;
      })
      .addCase(editBillAction.rejected, (state, action) => {
        state.editBillLoading = false;
        state.editBillError = action.error.message ?? "Something went wrong";
        state.editBillResponse = null;
      })
      .addCase(updateBillDiscountAction.pending, (state) => {
        state.updateBillDiscountLoading = true;
        state.updateBillDiscountError = null;
      })
      .addCase(updateBillDiscountAction.fulfilled, (state, action) => {
        state.updateBillDiscountLoading = false;
        state.updateBillDiscountError = null;

        const updated = action.payload;
        if (!updated) return;

        const patchBill = <T extends { id: number }>(bill: T): T =>
          bill.id === updated.id
            ? {
                ...bill,
                discountAmount: updated.discountAmount,
                discountId: updated.discountId,
                billAmount: updated.billAmount,
              }
            : bill;

        if (state.bills) {
          state.bills = state.bills.map(patchBill);
        }
        state.cartHistorySelectedRows = state.cartHistorySelectedRows.map(
          patchBill
        );
        // The invoice shown on the customer history page: its left summary
        // reads the saved discount from here.
        if (state.billById?.id === updated.id) {
          state.billById = updated;
        }
      })
      .addCase(updateBillDiscountAction.rejected, (state, action) => {
        state.updateBillDiscountLoading = false;
        state.updateBillDiscountError =
          (action.payload as string) || "Failed to update discount";
      })
      .addCase(saveDraftAction.pending, (state) => {
        state.saveDraftLoading = true;
        state.saveDraftError = null;
        state.saveDraftResponse = null;
      })
      .addCase(saveDraftAction.fulfilled, (state, action) => {
        state.saveDraftLoading = false;
        state.saveDraftError = null;
        state.saveDraftResponse = action.payload;
        if (action.payload) {
          state.drafts = [...state.drafts, action.payload];
        }
      })
      .addCase(saveDraftAction.rejected, (state, action) => {
        state.saveDraftLoading = false;
        state.saveDraftError = action.error.message ?? "Something went wrong";
        state.saveDraftResponse = null;
      })
      .addCase(getCashierDraftsAction.pending, (state) => {
        state.getCashierDraftsLoading = true;
        state.getCashierDraftsError = null;
        state.getCashierDrafts = null;
      })
      .addCase(getCashierDraftsAction.fulfilled, (state, action) => {
        state.getCashierDraftsLoading = false;
        state.getCashierDraftsError = null;
        state.getCashierDrafts = action.payload;
      })
      .addCase(getCashierDraftsAction.rejected, (state, action) => {
        state.getCashierDraftsLoading = false;
        state.getCashierDraftsError =
          action.error.message ?? "Something went wrong";
        state.getCashierDrafts = null;
      })
      .addCase(deleteDraftAction.pending, (state) => {
        state.deleteDraftLoading = true;
        state.deleteDraftError = null;
        state.deleteDraftResponse = null;
      })
      .addCase(deleteDraftAction.fulfilled, (state, action) => {
        state.deleteDraftLoading = false;
        state.deleteDraftResponse = action.payload;

        state.drafts = state.drafts.filter(
          (d: any) =>
            d.serverId !== action.payload && d.draftId !== action.payload
        );
      })
      .addCase(deleteDraftAction.rejected, (state, action) => {
        state.deleteDraftLoading = false;
        state.deleteDraftError = action.error.message ?? "Something went wrong";
        state.deleteDraftResponse = null;
      });
  },
});

export default cartSlice.reducer;
export const CartSliceAction = cartSlice.actions;
